import { getDeploymentRegenerateUrl } from '../routes';
import { getErrorMessage } from './errorUtils';
import {
  SitecoreContextResourceType,
  TenantApplicationNode,
  RegenerationErrorType,
} from '../types/types';

export interface ValidationResult {
  success: boolean;
  deploymentUrl?: string;
  errorMessage?: string;
  errorType?: RegenerationErrorType;
}

export const validateRegenerationAndGetUrl = (
  sitecoreContextId: string,
  resources: SitecoreContextResourceType[] | undefined,
  applicationsNodes: TenantApplicationNode[],
): ValidationResult => {
  try {
    const organizationId = getOrganizationId(resources);
    if (!organizationId) {
      return {
        success: false,
        errorMessage: getErrorMessage(
          RegenerationErrorType.MISSING_ORGANIZATION_ID,
        ),
        errorType: RegenerationErrorType.MISSING_ORGANIZATION_ID,
      };
    }

    const deploymentInfo = getDeploymentInfoSync(resources, applicationsNodes);
    if (!deploymentInfo) {
      return {
        success: false,
        errorMessage: getErrorMessage(RegenerationErrorType.NO_ANNOTATIONS),
        errorType: RegenerationErrorType.NO_ANNOTATIONS,
      };
    }

    const { projectId, environmentId } = deploymentInfo;

    if (!projectId && !environmentId) {
      return {
        success: false,
        errorMessage: getErrorMessage(RegenerationErrorType.NO_ANNOTATIONS),
        errorType: RegenerationErrorType.NO_ANNOTATIONS,
      };
    } else if (!projectId) {
      return {
        success: false,
        errorMessage: getErrorMessage(RegenerationErrorType.MISSING_PROJECT_ID),
        errorType: RegenerationErrorType.MISSING_PROJECT_ID,
      };
    } else if (!environmentId) {
      return {
        success: false,
        errorMessage: getErrorMessage(
          RegenerationErrorType.MISSING_ENVIRONMENT_ID,
        ),
        errorType: RegenerationErrorType.MISSING_ENVIRONMENT_ID,
      };
    }

    const formattedOrgId = organizationId.startsWith('org_')
      ? organizationId
      : 'org_' + organizationId;

    const deploymentUrl = getDeploymentRegenerateUrl(
      projectId,
      environmentId,
      formattedOrgId,
    );

    return {
      success: true,
      deploymentUrl,
    };
  } catch (error) {
    return {
      success: false,
      errorMessage:
        'An unexpected error occurred while processing the regeneration request.',
      errorType: RegenerationErrorType.GENERAL_ERROR,
    };
  }
};

const getOrganizationId = (
  resources: SitecoreContextResourceType[] | undefined,
): string | null => {
  const searchParams = new URLSearchParams(window.location.search);
  const organizationId = searchParams.get('organization');

  if (organizationId) {
    return organizationId;
  }

  const pathParts = window.location.pathname.split('/');
  for (let i = 0; i < pathParts.length; i++) {
    if (pathParts[i] === 'organization' && i + 1 < pathParts.length) {
      return pathParts[i + 1];
    }
  }

  if (resources && resources.length > 0) {
    const contextResource = resources.find((r) => r.organizationId);
    if (contextResource && contextResource.organizationId) {
      return contextResource.organizationId;
    }
  }

  return (
    localStorage.getItem('organizationId') ||
    document.cookie
      .split('; ')
      .find((row) => row.startsWith('organizationId='))
      ?.split('=')[1] ||
    null
  );
};

export const getDeploymentInfoSync = (
  resources: SitecoreContextResourceType[] | undefined,
  applicationsNodes: TenantApplicationNode[],
): { projectId: string; environmentId: string } | null => {
  if (!resources || resources.length === 0) {
    return null;
  }
  if (!applicationsNodes || applicationsNodes.length === 0) {
    return null;
  }

  try {
    const relevantResources = resources.filter(
      (r) =>
        r.tenantProductCode === 'Edge' || r.tenantProductCode === 'XMCloud',
    );

    if (relevantResources.length === 0) {
      return null;
    }

    const sortedResources = [...relevantResources].sort((a, b) => {
      const aHasSlash = a.tenantName?.includes('/') ? 1 : 0;
      const bHasSlash = b.tenantName?.includes('/') ? 1 : 0;
      return bHasSlash - aHasSlash;
    });

    for (const resource of sortedResources) {
      if (!resource.tenantId) {
        continue;
      }

      const tenantNode = applicationsNodes.find(
        (node) => node.id === resource.tenantId,
      );
      if (!tenantNode) {
        continue;
      }

      const extractedInfo = extractAnnotationValues(tenantNode);
      if (extractedInfo) {
        return extractedInfo;
      }
    }

    for (const node of applicationsNodes) {
      const projectId = node.annotations.find(
        (a) =>
          a.key === 'XMCloud.ProjectId' ||
          a.key === 'XmCloud.ProjectId' ||
          (a.key.toLowerCase().includes('project') &&
            a.key.toLowerCase().includes('id')),
      )?.value;

      const environmentId = node.annotations.find(
        (a) =>
          a.key === 'XMCloud.EnvironmentId' ||
          a.key === 'XmCloud.EnvironmentId' ||
          (a.key.toLowerCase().includes('environment') &&
            a.key.toLowerCase().includes('id')),
      )?.value;

      if (projectId && environmentId) {
        return { projectId, environmentId };
      }
    }

    return null;
  } catch (error) {
    return null;
  }
};

export const extractAnnotationValues = (
  tenantNode: TenantApplicationNode,
): {
  projectId: string;
  environmentId: string;
} | null => {
  const projectIdKeys = ['XMCloud.ProjectId', 'XmCloud.ProjectId'];
  const environmentIdKeys = ['XMCloud.EnvironmentId', 'XmCloud.EnvironmentId'];

  let projectId = '';
  let environmentId = '';

  for (const key of projectIdKeys) {
    const value = tenantNode.annotations.find((a) => a.key === key)?.value;
    if (value) {
      projectId = value;
      break;
    }
  }

  for (const key of environmentIdKeys) {
    const value = tenantNode.annotations.find((a) => a.key === key)?.value;
    if (value) {
      environmentId = value;
      break;
    }
  }

  if (!projectId) {
    const projectAnnotation = tenantNode.annotations.find(
      (a) =>
        a.key.toLowerCase().includes('project') &&
        a.key.toLowerCase().includes('id'),
    );
    if (projectAnnotation) {
      projectId = projectAnnotation.value;
    }
  }

  if (!environmentId) {
    const envAnnotation = tenantNode.annotations.find(
      (a) =>
        a.key.toLowerCase().includes('environment') &&
        a.key.toLowerCase().includes('id'),
    );
    if (envAnnotation) {
      environmentId = envAnnotation.value;
    }
  }

  if (!projectId || !environmentId) {
    return null;
  }

  return { projectId, environmentId };
};
